import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Layout from './components/Layout';
import Login from './pages/Login';
import Registro from './pages/Registro';
import SolicitarRecuperacion from './pages/SolicitarRecuperacion';
import RestablecerPassword from './pages/RestablecerPassword';
import Dashboard from './pages/Dashboard';
import Clientes from './pages/Clientes';
import Cotizaciones from './pages/Cotizaciones';
import Proyectos from './pages/Proyectos';
import ProyectoDetalle from './pages/ProyectoDetalle';
import Trabajadores from './pages/Trabajadores';
import Especialidades from './pages/Especialidades';
import Capataces from './pages/Capataces';
import Inventario from './pages/Inventario';
import Reportes from './pages/Reportes';
import Perfil from './pages/Perfil';
import AdminDashboard from './pages/AdminDashboard';
import AdminUsuarios from './pages/AdminUsuarios';
import AdminConfiguracion from './pages/AdminConfiguracion';
import './App.css';

// Componente de ruta protegida
const ProtectedRoute = ({ children }) => {
  const { usuario, loading } = useAuth();

  if (loading) {
    return <div className="loading-screen">Cargando...</div>;
  }

  if (!usuario) {
    return <Navigate to="/login" replace />;
  }

  return <Layout>{children}</Layout>;
};

// Componente de ruta protegida solo para administradores
const AdminRoute = ({ children }) => {
  const { usuario, loading, esAdmin } = useAuth();

  if (loading) {
    return <div className="loading-screen">Cargando...</div>;
  }

  if (!usuario) {
    return <Navigate to="/login" replace />;
  }

  if (!esAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Layout>{children}</Layout>;
};

// Componente de ruta pública (redirige si ya está logueado)
const PublicRoute = ({ children }) => {
  const { usuario, loading, esAdmin } = useAuth();

  if (loading) {
    return <div className="loading-screen">Cargando...</div>;
  }

  if (usuario) {
    // Redirigir según el rol del usuario
    return <Navigate to={esAdmin ? "/admin-dashboard" : "/dashboard"} replace />;
  }

  return children;
};

// Componente para redirección por defecto según el rol
const DefaultRedirect = () => {
  const { usuario, loading, esAdmin } = useAuth();

  if (loading) {
    return <div className="loading-screen">Cargando...</div>;
  }

  if (!usuario) {
    return <Navigate to="/login" replace />;
  }

  // Redirigir según el rol del usuario
  return <Navigate to={esAdmin ? "/admin-dashboard" : "/dashboard"} replace />;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Rutas públicas */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/registro"
        element={
          <PublicRoute>
            <Registro />
          </PublicRoute>
        }
      />
      <Route
        path="/solicitar-recuperacion"
        element={
          <PublicRoute>
            <SolicitarRecuperacion />
          </PublicRoute>
        }
      />
      <Route
        path="/recuperar-password/:token"
        element={
          <PublicRoute>
            <RestablecerPassword />
          </PublicRoute>
        }
      />

      {/* Rutas protegidas */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />

      {/* Páginas de gestión */}
      <Route
        path="/clientes"
        element={
          <ProtectedRoute>
            <Clientes />
          </ProtectedRoute>
        }
      />

      <Route
        path="/cotizaciones"
        element={
          <ProtectedRoute>
            <Cotizaciones />
          </ProtectedRoute>
        }
      />

      <Route
        path="/proyectos"
        element={
          <ProtectedRoute>
            <Proyectos />
          </ProtectedRoute>
        }
      />

      <Route
        path="/proyectos/:id"
        element={
          <ProtectedRoute>
            <ProyectoDetalle />
          </ProtectedRoute>
        }
      />

      <Route
        path="/trabajadores"
        element={
          <ProtectedRoute>
            <Trabajadores />
          </ProtectedRoute>
        }
      />

      <Route
        path="/especialidades"
        element={
          <ProtectedRoute>
            <Especialidades />
          </ProtectedRoute>
        }
      />

      <Route
        path="/capataces"
        element={
          <ProtectedRoute>
            <Capataces />
          </ProtectedRoute>
        }
      />

      <Route
        path="/inventario"
        element={
          <ProtectedRoute>
            <Inventario />
          </ProtectedRoute>
        }
      />

      <Route
        path="/reportes"
        element={
          <ProtectedRoute>
            <Reportes />
          </ProtectedRoute>
        }
      />

      <Route
        path="/perfil"
        element={
          <ProtectedRoute>
            <Perfil />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin-dashboard"
        element={
          <AdminRoute>
            <AdminDashboard />
          </AdminRoute>
        }
      />

      <Route
        path="/admin-usuarios"
        element={
          <AdminRoute>
            <AdminUsuarios />
          </AdminRoute>
        }
      />

      <Route
        path="/admin-configuracion"
        element={
          <AdminRoute>
            <AdminConfiguracion />
          </AdminRoute>
        }
      />

      {/* Ruta por defecto */}
      <Route path="/" element={<DefaultRedirect />} />
      <Route path="*" element={<DefaultRedirect />} />
    </Routes>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </Router>
  );
}

export default App;
